/**
 * The phone's log while somebody drives it, with their own actions interleaved.
 *
 * **Off until asked**, like audio and for a sharper reason: reading the log holds `adb logcat` open on
 * the same USB link that carries taps and frames, and repeated log reads were the main cause of laggy
 * control in the original lab. Nobody pays for a log they are not watching.
 *
 * Opening it **replays the session so far** — the hub asks the phone for what it still holds and the
 * service adds what has arrived since — so switching it on after ten minutes is useful rather than a
 * fresh empty page. The phone's buffer is the limit (~256 KiB), and the first marker line says how far
 * back the replay actually reached.
 *
 * One socket per (session, filter): changing the package or the match is a *different log*, so the
 * socket is reopened rather than filtered client-side — these machines are on mobile links and a busy
 * phone's whole log is not something to ship across one to be thrown away at the other end.
 */

import { useEffect, useRef, useState } from 'react'

import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { classify } from '../../lib/logLines'
import { Badge, Card, Empty, Status } from '../../ui/components'

const TIKTOK = 'com.zhiliaoapp.musically'
const MAX_LINES = 5000

interface Line {
  text: string
  kind: ReturnType<typeof classify> | 'marker'
}

export function LogPanel({ sessionId }: { sessionId: string | null }) {
  const { api } = useApp()
  const t = useT()
  const [on, setOn] = useState(false)
  const [pkg, setPkg] = useState(TIKTOK)
  const [match, setMatch] = useState('')
  const [applied, setApplied] = useState({ pkg: TIKTOK, match: '' })
  const [lines, setLines] = useState<Line[]>([])
  const [state, setState] = useState<'off' | 'connecting' | 'live' | 'closed'>('off')
  const [problem, setProblem] = useState<string | null>(null)
  const [follow, setFollow] = useState(true)
  const box = useRef<HTMLDivElement>(null)

  // Leaving a session turns the log off: the next phone must not inherit a filter someone forgot about.
  useEffect(() => {
    setOn(false)
    setLines([])
    setProblem(null)
  }, [sessionId])

  useEffect(() => {
    if (!on || !sessionId) {
      setState('off')
      return
    }
    setLines([])
    setProblem(null)
    setState('connecting')
    const qs = new URLSearchParams()
    if (applied.pkg) qs.set('package', applied.pkg)
    if (applied.match) qs.set('match', applied.match)
    const ws = new WebSocket(api.wsUrl(`/v1/sessions/${sessionId}/logs-ws?${qs.toString()}`))

    // Lines arrive in bursts of hundreds (the replay) and then ~100/s on a chatty app. One state update
    // per line would re-render the panel that often; they are batched on a short timer instead.
    let pending: Line[] = []
    const flush = setInterval(() => {
      if (!pending.length) return
      const add = pending
      pending = []
      setLines((prev) => {
        const next = prev.concat(add)
        return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next
      })
    }, 120)

    ws.onopen = () => setState('live')
    ws.onmessage = (ev) => {
      const m = JSON.parse(ev.data as string) as { type?: string; line?: string; error?: string; hint?: string; package?: string | null; match?: string | null; replaying?: number | null }
      if (m.type === 'line' && typeof m.line === 'string') pending.push({ text: m.line, kind: classify(m.line) })
      else if (m.type === 'start') pending.push({ text: t.logs.started(m.package ?? null, m.match ?? null, m.replaying ?? null), kind: 'marker' })
      else if (m.error) setProblem(m.hint ?? m.error)
    }
    ws.onclose = () => setState((s) => (s === 'live' || s === 'connecting' ? 'closed' : s))
    return () => {
      clearInterval(flush)
      ws.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, sessionId, on, applied])

  useEffect(() => {
    if (follow && box.current) box.current.scrollTop = box.current.scrollHeight
  }, [lines, follow])

  return (
    <Card
      title={t.logs.title}
      sub={sessionId ? undefined : t.logs.needSession}
      actions={
        <>
          {state === 'live' && <Badge kind="ok">{t.logs.live}</Badge>}
          {state === 'closed' && <Badge kind="warn">{t.logs.closed}</Badge>}
          <button
            className={on ? 'primary' : ''}
            aria-pressed={on}
            disabled={!sessionId}
            title={t.logs.toggleTitle}
            onClick={() => setOn((v) => !v)}
          >
            {on ? t.logs.turnOff : t.logs.turnOn}
          </button>
        </>
      }
    >
      {on && (
        <>
          <div className="row">
            <input
              className="grow"
              value={pkg}
              placeholder={t.logs.packagePlaceholder}
              aria-label={t.logs.packageLabel}
              onChange={(e) => setPkg(e.target.value)}
            />
            <input
              className="grow"
              value={match}
              placeholder={t.logs.matchPlaceholder}
              aria-label={t.logs.matchLabel}
              onChange={(e) => setMatch(e.target.value)}
            />
            <button
              disabled={pkg === applied.pkg && match === applied.match}
              onClick={() => setApplied({ pkg: pkg.trim(), match: match.trim() })}
            >
              {t.logs.apply}
            </button>
            <button className="ghost" onClick={() => setLines([])}>
              {t.logs.clear}
            </button>
            <label className="tiny dim">
              <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> {t.logs.follow}
            </label>
          </div>
          <Status text={problem} bad />
          <div
            className="log"
            ref={box}
            style={{ height: 320, marginTop: 'var(--sp-2)' }}
            onScroll={(e) => {
              const el = e.currentTarget
              setFollow(el.scrollHeight - el.scrollTop - el.clientHeight < 24)
            }}
          >
            {lines.length === 0 && state !== 'closed' && <Empty>{t.logs.waiting}</Empty>}
            {lines.map((l, i) => (
              <div key={i} className={`log-line log-${l.kind}`}>
                {l.text || ' '}
              </div>
            ))}
          </div>
          <span className="tiny dim">{t.logs.hint}</span>
        </>
      )}
      {!on && sessionId && <Empty>{t.logs.offHint}</Empty>}
    </Card>
  )
}
